import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { forceRefresh, skipWaiting } from '@/serviceWorkerRegistration';

/**
 * UpdateBanner component - Shows when a new version is available
 * Provides auto-refresh countdown or manual update option
 */
export function UpdateBanner({ registration, autoRefreshDelay = 30 }) {
    const [isVisible, setIsVisible] = useState(true);
    const [countdown, setCountdown] = useState(autoRefreshDelay);
    const [isPaused, setIsPaused] = useState(false);

    // Handle the update process
    const handleUpdate = useCallback(() => {
        // Skip waiting on the service worker if available
        if (registration) {
            skipWaiting(registration);
        }

        // Small delay to let SW activate, then reload
        setTimeout(() => {
            forceRefresh();
        }, 100);
    }, [registration]);

    // Countdown timer
    useEffect(() => {
        if (isPaused || countdown <= 0) {
            return;
        }

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    handleUpdate();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isPaused, countdown, handleUpdate]);

    // Pause countdown when user hovers (shows they're interacting)
    const handleMouseEnter = () => setIsPaused(true);
    const handleMouseLeave = () => setIsPaused(false);

    // Dismiss banner (pause auto-refresh)
    const handleDismiss = () => {
        setIsPaused(true);
        setIsVisible(false);
    };

    if (!isVisible) {
        // Show minimal floating button when dismissed
        return (
            <button
                onClick={handleUpdate}
                className="fixed bottom-28 md:bottom-8 right-4 z-[2000] bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors animate-pulse"
                title="Update available - Click to refresh"
            >
                <RefreshCw className="h-5 w-5" />
            </button>
        );
    }

    return (
        <div
            className="fixed bottom-[calc(70px+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 z-[2000] bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 shadow-lg"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="max-w-screen-md mx-auto flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                    <RefreshCw className="h-5 w-5 flex-shrink-0 animate-spin-slow" />
                    <div className="text-sm">
                        <span className="font-medium">New version available!</span>
                        {!isPaused && (
                            <span className="ml-2 text-blue-200">
                                Auto-updating in {countdown}s...
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleUpdate}
                        className="bg-white text-blue-600 hover:bg-blue-50"
                    >
                        Update Now
                    </Button>
                    <button
                        onClick={handleDismiss}
                        className="p-1 hover:bg-blue-500 rounded transition-colors"
                        title="Dismiss (update will still be available)"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

UpdateBanner.propTypes = {
    registration: PropTypes.object,
    autoRefreshDelay: PropTypes.number
};
